import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Col,
  Collapse,
  Divider,
  InputNumber,
  Row,
  Segmented,
  Slider,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type {
  AntifraudAuthenticityConfig,
  AntifraudConfig,
  AntifraudPreset,
  AntifraudReadingConfig,
} from '@shared/api/hooks/useTenantAdminConfig';

// ─── Preset defaults ──────────────────────────────────────────────────────────

type PresetValues = Omit<AntifraudConfig, 'preset'>;

const PRESET_DEFAULTS: Record<Exclude<AntifraudPreset, 'CUSTOM'>, PresetValues> = {
  PERMISSIVE: {
    reading: { autoApprove: 0.70, autoReject: 0.25, green: 0.60, yellow: 0.35 },
    authenticity: { green: 65, yellow: 40, softFloor: 25 },
    disparityThreshold: 40,
    phashHammingThreshold: 15,
  },
  BALANCED: {
    reading: { autoApprove: 0.78, autoReject: 0.35, green: 0.68, yellow: 0.42 },
    authenticity: { green: 72, yellow: 50, softFloor: 35 },
    disparityThreshold: 30,
    phashHammingThreshold: 10,
  },
  STRICT: {
    reading: { autoApprove: 0.88, autoReject: 0.50, green: 0.78, yellow: 0.55 },
    authenticity: { green: 82, yellow: 62, softFloor: 48 },
    disparityThreshold: 20,
    phashHammingThreshold: 5,
  },
};

/** Valore di default quando il tenant non ha ancora una configurazione. */
export const ANTIFRAUD_BALANCED_DEFAULT: AntifraudConfig = {
  preset: 'BALANCED',
  ...PRESET_DEFAULTS.BALANCED,
};

// ─── Preset metadata (label, colore, descrizione didattica) ──────────────────

const PRESET_META: Record<
  AntifraudPreset,
  { alertType: 'success' | 'info' | 'warning'; message: string; description: string }
> = {
  PERMISSIVE: {
    alertType: 'success',
    message: 'Permissivo — fluidità massima',
    description:
      'Soglie basse: più scontrini vengono auto-approvati e meno finiscono in coda di revisione manuale. '
      + 'Consigliato nelle fasi di lancio o quando il volume è elevato e il comportamento degli utenti è già provato.',
  },
  BALANCED: {
    alertType: 'info',
    message: 'Bilanciato — default consigliato',
    description:
      'Bilancia automazione e controllo: auto-approva gli scontrini ad alta confidenza, invia in revisione i casi '
      + 'dubbi e rifiuta automaticamente solo i casi chiaramente fraudolenti.',
  },
  STRICT: {
    alertType: 'warning',
    message: 'Severo — controllo massimo',
    description:
      'Soglie alte: solo gli scontrini con confidenza molto alta vengono auto-approvati; la maggior parte finisce in '
      + 'revisione manuale. Consigliato in caso di abusi frequenti o campagne ad alto rischio.',
  },
  CUSTOM: {
    alertType: 'info',
    message: 'Personalizzato',
    description:
      'Hai modificato manualmente una o più soglie. I valori sono attivi esattamente come li hai configurati.',
  },
};

// ─── Validazione ──────────────────────────────────────────────────────────────

/** Restituisce gli errori di validazione o un array vuoto se tutto è corretto. */
export function validateAntifraudConfig(cfg: AntifraudConfig): string[] {
  const errors: string[] = [];
  const r = cfg.reading;
  const a = cfg.authenticity;

  if (Math.round(r.autoReject * 100) >= Math.round(r.autoApprove * 100)) {
    errors.push(
      'Lettura: la soglia auto-rifiuto deve essere strettamente inferiore alla soglia auto-approvazione.',
    );
  }
  if (Math.round(r.yellow * 100) >= Math.round(r.green * 100)) {
    errors.push('Lettura: la soglia zona-gialla deve essere strettamente inferiore alla soglia zona-verde.');
  }
  if (a.yellow >= a.green) {
    errors.push('Veridicità: la soglia zona-gialla deve essere strettamente inferiore alla soglia zona-verde.');
  }
  return errors;
}

// ─── SliderField — campo etichettato con slider + inputnumber ────────────────

interface SliderFieldProps {
  label: string;
  tooltip: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  addonAfter?: string;
  marks?: Record<number, string>;
}

function SliderField({
  label,
  tooltip,
  value,
  onChange,
  min,
  max,
  step = 1,
  addonAfter = '%',
  marks,
}: SliderFieldProps): React.ReactElement {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 6 }}>
        <Typography.Text strong>{label}</Typography.Text>{' '}
        <Tooltip title={tooltip}>
          <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
        </Tooltip>
      </div>
      <Row gutter={12} align="middle">
        <Col flex="auto">
          <Slider
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={onChange}
            marks={marks}
          />
        </Col>
        <Col style={{ width: 108 }}>
          <InputNumber
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(v) => onChange(v ?? min)}
            addonAfter={addonAfter}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
    </div>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────

export interface AntifraudSettingsTabProps {
  /**
   * Valore proveniente dal server. Se null/undefined il componente parte
   * con il preset Bilanciato. Quando cambia (es. dopo un refetch) lo stato
   * interno viene resettato ai valori del server.
   */
  initialValue?: AntifraudConfig | null;
  /** Chiamata ad ogni modifica con il nuovo valore completo. */
  onChange: (value: AntifraudConfig) => void;
}

export function AntifraudSettingsTab({
  initialValue,
  onChange,
}: AntifraudSettingsTabProps): React.ReactElement {
  const [cfg, setCfg] = useState<AntifraudConfig>(initialValue ?? ANTIFRAUD_BALANCED_DEFAULT);

  // Sincronizza lo stato interno quando arriva il valore dal server (post-fetch).
  // Non chiama onChange per evitare loop: il parent aggiorna il draft nel proprio
  // useEffect, non da qui.
  useEffect(() => {
    if (initialValue) {
      setCfg(initialValue);
    }
  }, [initialValue]);

  // Aggiorna sia lo stato locale che notifica il parent.
  const update = useCallback(
    (next: AntifraudConfig) => {
      setCfg(next);
      onChange(next);
    },
    [onChange],
  );

  const handlePresetChange = useCallback(
    (preset: AntifraudPreset) => {
      if (preset === 'CUSTOM') {
        update({ ...cfg, preset: 'CUSTOM' });
      } else {
        update({ preset, ...PRESET_DEFAULTS[preset] });
      }
    },
    [cfg, update],
  );

  // Lettura: i valori sono in storage come 0..1; la UI li mostra come 0..100.
  const handleReadingChange = useCallback(
    (key: keyof AntifraudReadingConfig, displayValue: number) => {
      update({
        ...cfg,
        preset: 'CUSTOM',
        reading: { ...cfg.reading, [key]: displayValue / 100 },
      });
    },
    [cfg, update],
  );

  // Veridicità: già in scala 0..100, nessuna conversione.
  const handleAuthenticityChange = useCallback(
    (key: keyof AntifraudAuthenticityConfig, value: number) => {
      update({
        ...cfg,
        preset: 'CUSTOM',
        authenticity: { ...cfg.authenticity, [key]: value },
      });
    },
    [cfg, update],
  );

  const handleDisparityChange = useCallback(
    (v: number) => update({ ...cfg, preset: 'CUSTOM', disparityThreshold: v }),
    [cfg, update],
  );

  const handlePhashChange = useCallback(
    (v: number) => update({ ...cfg, preset: 'CUSTOM', phashHammingThreshold: v }),
    [cfg, update],
  );

  const validationErrors = validateAntifraudConfig(cfg);
  const meta = PRESET_META[cfg.preset];

  // Valori display: reading storage 0..1 → %, arrotondato all'intero.
  const r = cfg.reading;
  const a = cfg.authenticity;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {/* Selettore preset */}
      <div>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Scegli un livello predefinito o personalizza le singole soglie.
          Più &quot;Severo&quot; significa meno auto-approvazioni e più revisioni manuali.
          Modificando un campo ci si sposta automaticamente su &quot;Personalizzato&quot;.
        </Typography.Text>
        <Segmented<AntifraudPreset>
          block
          size="large"
          value={cfg.preset}
          onChange={handlePresetChange}
          options={[
            {
              value: 'PERMISSIVE',
              label: <span style={{ color: '#52c41a', fontWeight: 600 }}>Permissivo</span>,
            },
            {
              value: 'BALANCED',
              label: <span style={{ fontWeight: 600 }}>Bilanciato</span>,
            },
            {
              value: 'STRICT',
              label: <span style={{ color: '#fa8c16', fontWeight: 600 }}>Severo</span>,
            },
            {
              value: 'CUSTOM',
              label: <span style={{ color: '#722ed1', fontWeight: 600 }}>Personalizzato</span>,
            },
          ]}
        />
      </div>

      {/* Descrizione del preset corrente */}
      <Alert
        type={meta.alertType}
        showIcon
        message={meta.message}
        description={meta.description}
      />

      {/* Errori di validazione */}
      {validationErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Configurazione non valida — il salvataggio è bloccato"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      )}

      {/* Impostazioni avanzate (collassabile) */}
      <Collapse
        defaultActiveKey={cfg.preset === 'CUSTOM' ? ['advanced'] : []}
        items={[
          {
            key: 'advanced',
            label: (
              <Space>
                <span>Impostazioni avanzate</span>
                {cfg.preset === 'CUSTOM' && <Tag color="purple">Personalizzato</Tag>}
              </Space>
            ),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={0}>

                {/* ── Lettura scontrino ─────────────────────────────────── */}
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Lettura scontrino
                </Typography.Title>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Confidenza del motore OCR nel riconoscere il testo (0% = nessuna, 100% = certa).
                </Typography.Text>

                <SliderField
                  label="Soglia auto-approvazione"
                  tooltip="Punteggio di lettura ≥ questa soglia → lo scontrino viene approvato automaticamente senza revisione. Abbassarla aumenta le auto-approvazioni."
                  value={Math.round(r.autoApprove * 100)}
                  onChange={(v) => handleReadingChange('autoApprove', v)}
                  min={0}
                  max={100}
                  marks={{ 0: '0%', 50: '50%', 100: '100%' }}
                />
                <SliderField
                  label="Soglia auto-rifiuto"
                  tooltip="Punteggio di lettura ≤ questa soglia → lo scontrino viene rifiutato automaticamente. Alzarla aumenta i rifiuti automatici. Deve essere inferiore alla soglia di auto-approvazione."
                  value={Math.round(r.autoReject * 100)}
                  onChange={(v) => handleReadingChange('autoReject', v)}
                  min={0}
                  max={100}
                  marks={{ 0: '0%', 50: '50%', 100: '100%' }}
                />
                <SliderField
                  label="Zona verde — alta confidenza"
                  tooltip="Punteggi ≥ questa soglia cadono in zona verde: lettura affidabile, probabile scontrino autentico."
                  value={Math.round(r.green * 100)}
                  onChange={(v) => handleReadingChange('green', v)}
                  min={0}
                  max={100}
                />
                <SliderField
                  label="Zona gialla — confidenza media"
                  tooltip="Punteggi compresi tra questa soglia e quella verde cadono in zona gialla: lettura incerta, richiede attenzione. Deve essere inferiore alla soglia zona-verde."
                  value={Math.round(r.yellow * 100)}
                  onChange={(v) => handleReadingChange('yellow', v)}
                  min={0}
                  max={100}
                />

                <Divider />

                {/* ── Veridicità ────────────────────────────────────────── */}
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Veridicità (OCR vs. metadati)
                </Typography.Title>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Confronto tra i dati OCR estratti e i metadati di contesto (importo, data, esercente).
                  Scala 0–100.
                </Typography.Text>

                <SliderField
                  label="Soglia verde — alta corrispondenza"
                  tooltip="Score di veridicità ≥ questa soglia: i dati OCR corrispondono bene ai metadati attesi."
                  value={a.green}
                  onChange={(v) => handleAuthenticityChange('green', v)}
                  min={0}
                  max={100}
                />
                <SliderField
                  label="Soglia gialla — corrispondenza parziale"
                  tooltip="Score tra questa soglia e quella verde: corrispondenza parziale (es. piccolo errore di digitazione o OCR impreciso). Deve essere inferiore alla soglia zona-verde."
                  value={a.yellow}
                  onChange={(v) => handleAuthenticityChange('yellow', v)}
                  min={0}
                  max={100}
                />
                <SliderField
                  label="Pavimento segnali soft"
                  tooltip="Score minimo affinché un segnale soft (non bloccante) venga considerato significativo. Segnali sotto questo valore vengono ignorati."
                  value={a.softFloor}
                  onChange={(v) => handleAuthenticityChange('softFloor', v)}
                  min={0}
                  max={100}
                />

                <Divider />

                {/* ── Disparità e duplicati ─────────────────────────────── */}
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Disparità e duplicati
                </Typography.Title>

                <SliderField
                  label="Soglia disparità (R alto, V basso)"
                  tooltip="Se il punteggio di lettura (R) è alto ma quello di veridicità (V) è basso, la differenza che supera questa soglia segnala lo scontrino come sospetto. Abbassarla rende il sistema più sensibile."
                  value={cfg.disparityThreshold}
                  onChange={handleDisparityChange}
                  min={0}
                  max={100}
                />
                <SliderField
                  label="Soglia somiglianza immagini (pHash Hamming)"
                  tooltip="Distanza di Hamming tra gli hash percettivi (pHash) di due immagini. Più è bassa, più le immagini devono essere simili per essere classificate come duplicate. 0 = identiche, 64 = completamente diverse."
                  value={cfg.phashHammingThreshold}
                  onChange={handlePhashChange}
                  min={0}
                  max={64}
                  addonAfter="bit"
                />

              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
